import * as H from "history";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { ProcessorItem } from "../../../../models/MailroomProcessorItem";
import { IRootState } from "../../../../store";
import ApiService from "../../../ApiService";
import ProcessorItemsViewer from "../../../mailroom/ProcessorItemsViewer";
import { CommandBarButton, MessageBarType } from "office-ui-fabric-react";
import DispatchProps from "../../../../utils/DispatchProps";
import Actions from "../../../../redux/Actions";
import { DurableFunctionInstance, DurableFunctionInstanceHelpers } from "../../../../models/DurableFunctionInstance";
import MailroomDurableFunctionInstanceList from "../../../mailroom/MailroomDurableFunctionInstanceList";
import { Link } from "react-router-dom";
import ProcessorResultsCharts from "../../../mailroom/ProcessorResultsCharts";
import ProcessorErrorsCharts from "../../../mailroom/ProcessorErrorsCharts";
import JsonViewerCard from "../../../JsonViewerCard";
import MailroomDurableFunctionInstanceHistory from "../../../mailroom/MailroomDurableFunctionInstanceHistory";
import ProcessorComparisionSelector from "../../../ProcessorComparisonSelector";
import { openFileDownload } from "../../../../utils/Utils";
import { UserAccessLevel } from "../../../../models/UserAccessRecord";
import SchoolPageTitle from "../../../SchoolPageTitle";
import AdminSchoolMenu from "../../../AdminSchoolMenu";

interface ProcessorInstancePageProps {
    tenantId: string;
    schoolCode: string;
    instanceId: string;

    usersCdbAccessLevel?: UserAccessLevel;
    apiService: ApiService;
    history: H.History,
}

interface ProcessorInstancePageState {
    instance: DurableFunctionInstance | undefined | null;
    items?: ProcessorItem[];
    showResultsCharts: boolean;
    showErrorsCharts: boolean;
    showProcessorInput: boolean;
    showProcessorHistory: boolean;
}

class ProcessorInstancePage extends Component<ProcessorInstancePageProps & DispatchProps, ProcessorInstancePageState> {

    constructor(props: ProcessorInstancePageProps & DispatchProps) {
        super(props);

        this.state = {
            instance: undefined,
            items: undefined,
            showResultsCharts: false,
            showErrorsCharts: false,
            showProcessorInput: false,
            showProcessorHistory: false
        };
    }

    async componentDidUpdate(prevProps: Readonly<ProcessorInstancePageProps & DispatchProps>, prevState: Readonly<ProcessorInstancePageState>, snapshot?: any): Promise<void> {
        if (this.props.instanceId !== prevProps.instanceId) {
            await this.loadInstanceAndOutput(this.props.instanceId);
        }
    }

    async componentDidMount() {
        if (this.props.instanceId) {
            await this.loadInstanceAndOutput(this.props.instanceId);
        }
    }


    private async loadInstanceAndOutput(instanceId: string) {

        this.setState({
            instance: undefined
        });

        const instance = await this.props.apiService.getMailroomFunctionInstance(instanceId);
        this.setState({
            instance
        });

        if (instance.runtimeStatus === "Completed" || instance.runtimeStatus === "Failed") {
            const output = DurableFunctionInstanceHelpers.ParseOutput(instance);

            let processorItems: ProcessorItem[] = [];
            if (output.ProcessorItemsID !== undefined && output.ProcessorItemsID !== null) {
                processorItems = await this.props.apiService.getMailroomProcessorItems(output.ProcessorItemsID);
            }

            this.setState({
                instance,
                items: processorItems
            });
        }
    }

    private getCSV = async (instanceId: string) => {
        try {
            const result = await this.props.apiService.getSdsCSV(instanceId);

            await openFileDownload(result)

            this.props.dispatch(Actions.showMessageBar(`Succesfully got CSVs`, MessageBarType.success));
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error getting CSVs`, MessageBarType.error));
        }
    }


    private buildButtons() {

        if (!this.state.instance) {
            return null;
        }


        const buttons = [];

        const showPorcessorHistoryButton = (
            <CommandBarButton
                iconProps={{ iconName: "History" }}
                text="Show Processor History"
                onClick={() => {
                    this.setState({
                        showProcessorHistory: true
                    });
                }}
            />
        );
        buttons.push(showPorcessorHistoryButton);

        const showInputButton = (
            <CommandBarButton
                iconProps={{ iconName: "MailOptions" }}
                text="Show Processor Input"
                onClick={() => {
                    this.setState({
                        showProcessorInput: true
                    });
                }}
            />
        );
        buttons.push(showInputButton);


        if (this.state.instance.runtimeStatus === "Completed") {

            const showCompareSelector = (
                <ProcessorComparisionSelector tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} instanceIdA={this.state.instance?.durableFunctionInstanceId!} history={this.props.history} />
            );
            buttons.push(showCompareSelector);

            
            const downloadCsvButton = (
                <CommandBarButton
                    iconProps={{ iconName: "DownloadDocument" }}
                    text="Download SDS CSV archive"
                    onClick={() => this.getCSV(this.props.instanceId)}
                />
            );
            buttons.push(downloadCsvButton);

            const previewPhotosButton = (
                <Link to={`/cdb-admin/mailroom/salamander/${this.props.tenantId}/${this.props.schoolCode}/process/${this.props.instanceId}/previewPhotos`} target="_blank">
                    <CommandBarButton
                        iconProps={{ iconName: "PhotoCollection" }}
                        text="Preview Photos"
                    />

                </Link>
            );
            buttons.push(previewPhotosButton);
        }

        if (this.state.instance.runtimeStatus === "Completed" || this.state.instance.runtimeStatus === "Failed") {
            const deleteButton = (
                <CommandBarButton
                    iconProps={{ iconName: "trash" }}
                    text="Delete"
                    onClick={() => this.onPurgeInstance()} />
            );
            buttons.push(deleteButton);
        }

        return (<Fragment>{buttons}</Fragment>);
    }


    private async onPurgeInstance() {
        try {
            await this.props.apiService.purgeMailroomFunctionInstance(this.props.instanceId);
            this.props.dispatch(Actions.showMessageBar(`Successfully deleted processor history`, MessageBarType.success));
            this.props.history.push(`/cdb-admin/mailroom/salamander/${this.props.tenantId}/${this.props.schoolCode}/process`);
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error deleting processor history`, MessageBarType.error));
        }
    }

    private buildProcessorInputJsonCard(): JSX.Element {
        return (
            <JsonViewerCard title="Processor Input" jsonObject={this.state.instance!.input}>
                <CommandBarButton
                    iconProps={{ iconName: "Cancel" }}
                    text="Close"
                    onClick={() => {
                        this.setState({
                            showProcessorInput: false
                        })
                    }}
                />
            </JsonViewerCard>
        );
    }

    private buildHistory() {
        return <MailroomDurableFunctionInstanceHistory title={"Processor History"} instanceId={this.props.instanceId}>
            <CommandBarButton
                iconProps={{ iconName: "Cancel" }}
                text="Close"
                onClick={() => {
                    this.setState({
                        showProcessorHistory: false
                    })
                }}
            />
        </MailroomDurableFunctionInstanceHistory>
    }

    render() {

        const errorsCharts = (
            <ProcessorErrorsCharts processorItems={this.state.items} onCloseClicked={() => {
                this.setState({
                    showErrorsCharts: false
                })
            }} />
        )

        const resultsCharts = (
            <ProcessorResultsCharts processorItems={this.state.items} onCloseClicked={() => {
                this.setState({ showResultsCharts: false })
            }} />
        );

        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Processor" /> 

                <AdminSchoolMenu tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} />
                
                <MailroomDurableFunctionInstanceList title={"Instance"} columns={MailroomDurableFunctionInstanceList.processorColumns} additionalCellForColumn={MailroomDurableFunctionInstanceList.processorAdditionalCellForColumn} enableRowLinks={false} instances={this.state.instance ? [this.state.instance] : undefined} >
                    {this.buildButtons()}
                </MailroomDurableFunctionInstanceList>

                {this.state.showProcessorHistory ? this.buildHistory() : null}
                {this.state.showProcessorInput ? this.buildProcessorInputJsonCard() : null}
                {this.state.showResultsCharts ? resultsCharts : null}
                {this.state.showErrorsCharts ? errorsCharts : null}
                <ProcessorItemsViewer processorItems={this.state.items}>

                    <CommandBarButton
                        iconProps={{ iconName: "DonutChart" }}
                        text="Chart Errors"
                        onClick={() => {
                            this.setState({
                                showErrorsCharts: true
                            });
                        }}
                    />
                    <CommandBarButton
                        iconProps={{ iconName: "DonutChart" }}
                        text="Chart Results"
                        onClick={() => {
                            this.setState({
                                showResultsCharts: true
                            });
                        }}
                    />
                </ProcessorItemsViewer>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History, match: any }): ProcessorInstancePageProps => {

        return {
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            instanceId: ownProps.match.params.instanceId,

            usersCdbAccessLevel: state.auth.usersCdbAccessLevel,
            apiService: state.auth.apiService,
            history: state.history
        };
    }
)(ProcessorInstancePage);